import { useRef, useState } from "react";
import { ArrowRight, ArrowUpRight, Clock, ExternalLink, Link2 } from "lucide-react";
import { BLOG_PATH, blogCategories, blogPosts, filterBlogPosts, relatedBlogPosts, slugifyHeading, type BlogPost } from "@/data/blog";

/** Join a site base (e.g. "/" or "/app/") with an internal path. External URLs pass through. */
export function withBase(base: string, href: string) {
  if (/^(https?:|mailto:|tel:)/.test(href)) return href;
  return base.replace(/\/+$/, "") + (href.startsWith("/") ? href : `/${href}`);
}

const catId = (c: string) => `category-${slugifyHeading(c)}`;

export function BlogIndexContent({ base = "/", guideHubHref = "/sauna/guides", quoteHref = "/quote?category=sauna" }: { base?: string; guideHubHref?: string; quoteHref?: string }) {
  const u = (h: string) => withBase(base, h);
  // Empty initial state keeps every article in both server HTML and the first client render.
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const searchInput = useRef<HTMLInputElement>(null);
  const postsFound = filterBlogPosts(query, category);
  const hasFilters = query !== "" || category !== "";
  const resetFilters = () => {
    setQuery("");
    setCategory("");
    searchInput.current?.focus();
  };
  return (
    <>
      <section className="border-b border-white/10 bg-[hsl(var(--vx-blue))] text-white">
        <div className="container mx-auto max-w-6xl px-4 py-12 lg:py-16 grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">Advice library</p>
            <h1 className="mt-2 font-display text-4xl lg:text-5xl font-bold tracking-tight" data-testid="text-blog-title">Blog &amp; Guides</h1>
            <p className="mt-4 max-w-2xl text-white/90 leading-relaxed">
              Practical sauna articles on design, equipment, installation and care. Written to help you prepare a useful enquiry, not to replace a site assessment.
            </p>
          </div>
          <a href={u(guideHubHref)} className="group inline-flex w-fit items-center gap-2 rounded-full border border-white/30 bg-white px-4 py-2 text-sm font-semibold text-[hsl(var(--vx-blue))] hover:bg-sky-100 transition-colors" data-testid="link-blog-guide-index">
            Sauna guide hub <ArrowRight className="h-4 w-4 motion-safe:transition-transform group-hover:translate-x-0.5" aria-hidden />
          </a>
        </div>
      </section>

      <section aria-label="Find articles" className="border-b border-border/70 bg-background">
        <div className="container mx-auto max-w-6xl px-4 py-5">
          <div role="search" className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
            <div className="min-w-0">
              <label htmlFor="blog-search" className="block text-sm font-semibold">Search articles</label>
              <input ref={searchInput} id="blog-search" type="search" value={query} onChange={event => setQuery(event.target.value)}
                placeholder="Search sauna topics…" aria-controls="blog-results" aria-describedby="blog-search-help"
                className="mt-2 min-h-11 w-full rounded-lg border border-border bg-background px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="input-blog-search" />
            </div>
            <div className="min-w-0">
              <label htmlFor="blog-category" className="block text-sm font-semibold">Category</label>
              <select id="blog-category" value={category} onChange={event => setCategory(event.target.value)} aria-controls="blog-results"
                className="mt-2 min-h-11 w-full rounded-lg border border-border bg-background px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="select-blog-category">
                <option value="">All categories</option>
                {blogCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <button type="button" onClick={resetFilters} disabled={!hasFilters}
              className="min-h-11 rounded-lg px-4 text-sm font-semibold text-primary hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="button-blog-reset">Reset filters</button>
          </div>
          <p id="blog-search-help" className="mt-2 text-sm text-muted-foreground">Search titles, descriptions and topic keywords, or choose a category.</p>
          <p role="status" aria-live="polite" aria-atomic="true" className="mt-3 text-sm text-muted-foreground" data-testid="text-blog-results">
            Showing {postsFound.length} of {blogPosts.length} articles{category ? ` in ${category}` : ""}.
          </p>
        </div>
      </section>

      <div id="blog-results" className="container mx-auto max-w-6xl px-4 py-10 lg:py-14 space-y-14">
        {postsFound.length === 0 && (
          <section className="rounded-2xl bg-secondary/50 p-6 text-center" aria-labelledby="blog-empty-title" data-testid="blog-empty">
            <h2 id="blog-empty-title" className="font-display text-2xl font-bold">No articles found</h2>
            <p className="mt-2 text-muted-foreground">Try a different search term or category, or reset the filters to see all articles.</p>
            <button type="button" onClick={resetFilters} className="mt-4 min-h-11 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">Show all articles</button>
          </section>
        )}
        {blogCategories.map(c => {
          const posts = postsFound.filter(p => p.category === c);
          if (posts.length === 0) return null;
          return (
            <section key={c} id={catId(c)} aria-labelledby={`${catId(c)}-h`} className="scroll-mt-24">
              <div className="mb-5 flex items-baseline justify-between gap-4 border-b border-border/70 pb-3">
                <h2 id={`${catId(c)}-h`} className="font-display text-2xl font-bold tracking-tight">{c}</h2>
                <span className="text-sm text-muted-foreground">{posts.length} {posts.length === 1 ? "article" : "articles"}</span>
              </div>
              <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {posts.map(p => <BlogCard key={p.slug} post={p} base={base} />)}
              </ul>
            </section>
          );
        })}
      </div>

      <section className="container mx-auto max-w-6xl px-4 pb-14">
        <div className="flex flex-col gap-4 rounded-2xl bg-secondary/50 p-6 md:flex-row md:items-center md:justify-between">
          <p className="text-muted-foreground"><span className="font-semibold text-foreground">Planning a sauna?</span> Send measurements, photographs and your postcode for a tailored quotation.</p>
          <a href={u(quoteHref)} className="inline-flex w-fit items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground" data-testid="link-blog-quote">Request a quote <ArrowRight className="h-4 w-4" aria-hidden /></a>
        </div>
      </section>
    </>
  );
}

export function BlogCard({ post, base = "/" }: { post: BlogPost; base?: string }) {
  return (
    <li>
      <a href={withBase(base, post.path)} data-testid={`card-blog-${post.slug}`} className="group flex h-full flex-col rounded-2xl bg-card p-2 ring-1 ring-border/80 hover:ring-primary/40 motion-safe:hover:-translate-y-0.5 transition-all">
        <span className="block aspect-[16/9] overflow-hidden rounded-xl bg-secondary">
          <img src={withBase(base, `/images/${post.image}`)} alt="" loading="lazy" className="h-full w-full object-cover motion-safe:transition-transform duration-700 motion-safe:group-hover:scale-[1.05]" />
        </span>
        <span className="flex flex-1 flex-col px-2.5 pb-2.5 pt-3.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">{post.category}</span>
          <span className="mt-1.5 font-display text-[16px] font-bold leading-snug group-hover:text-primary transition-colors">{post.title}</span>
          <span className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground line-clamp-3">{post.metaDescription}</span>
          <span className="mt-auto pt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" aria-hidden />{post.readingMinutes} min read</span>
        </span>
      </a>
    </li>
  );
}

export function BlogArticleContent({ post, base = "/" }: { post: BlogPost; base?: string }) {
  const u = (h: string) => withBase(base, h);
  const related = relatedBlogPosts(post, 3);
  const toc = post.sections.map(s => ({ id: slugifyHeading(s.heading), heading: s.heading }));
  return (
    <article className="pb-16" data-testid={`article-blog-${post.slug}`}>
      <header className="bg-[hsl(var(--vx-blue))] text-white">
        <div className="container mx-auto max-w-5xl px-4 pt-8 pb-10 lg:pt-10 lg:pb-14">
          <nav aria-label="Breadcrumb" className="text-sm text-white/75">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><a href={u("/")} className="hover:text-white">Home</a></li>
              <li aria-hidden>/</li>
              <li><a href={u(BLOG_PATH)} className="hover:text-white" data-testid="link-breadcrumb-blog">Blog</a></li>
              <li aria-hidden>/</li>
              <li className="text-white/95 line-clamp-1" aria-current="page">{post.category}</li>
            </ol>
          </nav>
          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">{post.category}</p>
          <h1 className="mt-2 max-w-3xl font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-[1.1] tracking-tight" data-testid="text-article-title">{post.title}</h1>
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-white/80"><Clock className="h-4 w-4" aria-hidden />{post.readingMinutes} min read</p>
        </div>
      </header>

      <div className="container mx-auto max-w-5xl px-4">
        <figure className="-mt-0 lg:-mt-6 overflow-hidden rounded-b-2xl lg:rounded-2xl bg-secondary">
          <img src={u(`/images/${post.image}`)} alt={post.imageAlt} className="aspect-[16/7] w-full object-cover" data-testid="img-article-hero" />
        </figure>

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_240px]">
          <div className="min-w-0 max-w-[68ch]">
            <p className="text-lg leading-relaxed text-foreground/90">{post.intro}</p>

            {post.sections.map((s, i) => (
              <section key={i} aria-labelledby={toc[i].id} className="mt-10">
                <h2 id={toc[i].id} className="group scroll-mt-24 font-display text-2xl font-bold tracking-tight">
                  {s.heading}
                  <a href={`#${toc[i].id}`} className="ml-2 inline-flex align-middle text-muted-foreground opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity" aria-label={`Link to section: ${s.heading}`}>
                    <Link2 className="h-4 w-4" aria-hidden />
                  </a>
                </h2>
                {s.paragraphs.map((p, j) => <p key={j} className="mt-4 leading-relaxed text-foreground/85">{p}</p>)}
                {s.bullets && s.bullets.length > 0 && (
                  <ul className="mt-5 space-y-2 rounded-xl bg-secondary/50 p-5">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex gap-3 leading-relaxed"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />{b}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <aside className="mt-12 rounded-2xl bg-[hsl(var(--vx-blue))] p-6 sm:p-8 text-white" aria-labelledby="article-cta">
              <h2 id="article-cta" className="font-display text-2xl font-bold">{post.cta.heading}</h2>
              <p className="mt-3 text-white/90 leading-relaxed">{post.cta.text}</p>
              <a href={u(post.cta.href)} className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[hsl(var(--vx-blue))] hover:bg-sky-100 transition-colors" data-testid="link-article-cta">
                {post.cta.label} <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
            </aside>

            {post.sources && post.sources.length > 0 && (
              <section className="mt-10 border-t border-border/70 pt-6" aria-labelledby="article-sources">
                <h2 id="article-sources" className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">Sources and further reading</h2>
                <ul className="mt-3 space-y-2 text-sm">
                  {post.sources.map(s => (
                    <li key={s.href}><a href={s.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-primary underline-offset-4 hover:underline">{s.label}<ExternalLink className="h-3.5 w-3.5" aria-hidden /><span className="sr-only">(opens in a new tab)</span></a></li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
            {toc.length > 2 && (
              <nav aria-labelledby="article-toc" className="hidden lg:block">
                <h2 id="article-toc" className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">In this article</h2>
                <ol className="mt-3 space-y-2 border-l border-border pl-4 text-sm">
                  {toc.map(t => <li key={t.id}><a href={`#${t.id}`} className="text-foreground/75 hover:text-primary">{t.heading}</a></li>)}
                </ol>
              </nav>
            )}
            {post.serviceLinks.length > 0 && (
              <nav aria-labelledby="article-services">
                <h2 id="article-services" className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Related services</h2>
                <ul className="mt-3 space-y-2">
                  {post.serviceLinks.map(l => (
                    <li key={l.href}><a href={u(l.href)} className="group flex items-center justify-between gap-2 rounded-lg bg-secondary/60 px-3 py-2.5 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`link-service-${slugifyHeading(l.label)}`}>{l.label}<ArrowUpRight className="h-4 w-4 shrink-0" aria-hidden /></a></li>
                  ))}
                </ul>
              </nav>
            )}
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-16 border-t border-border/70 pt-10" aria-labelledby="article-related">
            <div className="mb-5 flex items-baseline justify-between gap-4">
              <h2 id="article-related" className="font-display text-2xl font-bold tracking-tight">Related articles</h2>
              <a href={u(BLOG_PATH)} className="text-sm font-semibold text-primary hover:underline underline-offset-4" data-testid="link-all-articles">All articles</a>
            </div>
            <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map(p => <BlogCard key={p.slug} post={p} base={base} />)}
            </ul>
          </section>
        )}
      </div>
    </article>
  );
}
